/**
 * Компонент не завершен
 * Экспериментальная копия reactApp/components/SharingWindow/SharingContent/SharingContent.tsx
 * Изменения относительно оригинала помечены комментарием - tempexp_17779
 **/

import { Icon20ChevronLeft } from '@vkontakte/icons';
import { Button } from '@vkontakte/vkui';
import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type ForwardedRef, forwardRef, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { isAbNewSharingFolder } from 'reactApp/appHelpers/featuresHelpers';
import { SharingNewWeblinkTab } from 'reactApp/components/SharingWindow/blocks/Weblink/SharingNewWeblinkTab';
import { SharingWeblinkIndividualAccessTab } from 'reactApp/components/SharingWindow/blocks/Weblink/SharingWeblinkIndividualAccessTab';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { useItemById } from 'reactApp/components/SharingWindow/hooks/useItemById.hook';
import { useWeblinkOpened } from 'reactApp/components/SharingWindow/hooks/useWeblinkOpened.hook';
import { getTitle } from 'reactApp/components/SharingWindow/Sharing.constants';
import styles from 'reactApp/components/SharingWindow/Sharing.css';
import {
    type SendSharingContentAnalytics,
    type SharingContentProps,
    EFrom,
    SharingTabs,
} from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingNewSharingTab } from 'reactApp/components/SharingWindow/ui/SharingTab/SharingNewSharingTab';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { isOwnPublic as isOwnPublicSelector } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isDomainFolder, isMountedFolder } from 'reactApp/modules/storage/folder.helpers';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { TabMenu } from 'reactApp/ui/Menu/TabMenu';
import { ETabMenuAlign, ETabMenuMod } from 'reactApp/ui/Menu/TabMenu.types';
import { TabMenuItem } from 'reactApp/ui/Menu/TabMenuItem';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { sendXray } from 'reactApp/utils/ga';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import { useTabsState } from './utils';

const TABS = {
    [SharingTabs.WEBLINK]: {
        name: 'По ссылке',
    },
    [SharingTabs.CLOUD_USERS]: {
        name: 'Пользователям облака',
    },
    [SharingTabs.CLOUD_USERS_BIZ]: {
        name: 'Пользователям Диска',
    },
    [SharingTabs.WEBLINK_ACCESS_LIST]: {
        name: 'По ссылке',
    },
};

interface Props extends SharingContentProps {
    onBackClick?: () => void;
}

export const SharingContentFolders = memo(
    // eslint-disable-next-line complexity, max-lines-per-function
    forwardRef((props: IPropsWithPopup<Props>, ref: ForwardedRef<HTMLDivElement>) => {
        const { itemStorage, id, from, onClose, gaSuffix, isPhone, withPromoTooltip, onboardingEnabled, folderName, variant, onBackClick } =
            props;
        const refRoot = useRef<HTMLDivElement | null>(null);

        const storage = useSelector(getCurrentStorage);
        const { isSharedIncoming, isStart, isStock, isInlineIntegration } = getStorage(storage);
        const isBizUser = useSelector(UserSelectors.isBizUser);

        // tempexp_17779-start
        const { fetchedDataOnMount, activeTab, setActiveTab, setFetchedDataOnMount } = useTabsState({ isSharedIncoming, from, variant });
        // tempexp_17779-end

        const item = useItemById(id, itemStorage);

        const isPublished = Boolean(item?.weblink);
        const { isWeblinkOpened } = useWeblinkOpened(from, isPublished);

        const isFolder = item?.isFolder;
        const isMounted = isMountedFolder(item);
        const isSharedOrMouted = useSelector((state: RootState) =>
            isFolder ? isMountedOrSharedFolder(state, item) : ({} as ReturnType<typeof isMountedOrSharedFolder>)
        );
        const isDomainItem: boolean = isDomainFolder(item);

        const isPublic = !!config.get('PUBLIC');
        const isOwnPublic = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));
        const isAlbum = from === EFrom.ALBUMS || storage === EStorageType.albums;
        const noEdit = isStock ? false : isPublic && !isOwnPublic;

        // tempexp_15661-start
        const abSharing = Boolean(!isPhone && isAbNewSharingFolder && item?.name && (isFolder || isAlbum) && !isMounted);
        const title = getTitle({
            isPublic: isStock ? false : isPublic,
            isMounted,
            name: item?.name,
            isAbNewSharingFolder: abSharing,
            isSharingTabsFeature: true,
            variant,
        });
        // tempexp_15661-end

        const { availableTabs, showSharing } = useMemo(() => {
            const availableTabs: Partial<typeof TABS> = {};

            if (variant === SHARING_VARIANT.foldersBiz) {
                availableTabs[SharingTabs.WEBLINK_ACCESS_LIST] = TABS[SharingTabs.WEBLINK_ACCESS_LIST];
            } else if (!isSharedIncoming) {
                availableTabs[SharingTabs.WEBLINK] = TABS[SharingTabs.WEBLINK];
            }

            const showSharing =
                !isAlbum &&
                isFolder &&
                !isDomainItem &&
                !isStart &&
                (!(isSharedOrMouted.isMounted || isSharedOrMouted.isShared) || item?.id === isSharedOrMouted.folderId) &&
                item?.kind !== 'family' &&
                !isInlineIntegration;

            if (showSharing) {
                if (isBizUser) {
                    availableTabs[SharingTabs.CLOUD_USERS_BIZ] = TABS[SharingTabs.CLOUD_USERS_BIZ];
                } else {
                    availableTabs[SharingTabs.CLOUD_USERS] = TABS[SharingTabs.CLOUD_USERS];
                }
            }

            return { availableTabs, showSharing };
        }, [
            isAlbum,
            isBizUser,
            isDomainItem,
            isFolder,
            isInlineIntegration,
            isSharedIncoming,
            isSharedOrMouted.folderId,
            isSharedOrMouted.isMounted,
            isSharedOrMouted.isShared,
            isStart,
            item?.id,
            item?.kind,
            variant,
        ]);

        const onCloseAccess = useCallback(() => {
            if (storage === EStorageType.sharedLinks) {
                onClose();
            }
        }, [storage, onClose]);

        const sendSharingContentAnalytics = useCallback<SendSharingContentAnalytics>(({ action, label }) => {
            sendPaymentGa({ eventCategory: 'enable-sharing', action, label });
            emitAnalyticEvent(AnalyticEventNames.ENABLE_SHARING_SHOW);
        }, []);

        useEffect(() => {
            sendSharingContentAnalytics({ action: 'show' });
        }, [sendSharingContentAnalytics]);

        useEffect(() => {
            const xrayUserType = isBizUser ? 'biz' : 'b2c';

            sendXray(['sharing-content-tabs-mount', xrayUserType]);
        }, []);

        return (
            <div className={classNames(styles.root, { [styles.root_phone]: isPhone })} ref={refRoot}>
                {onBackClick && isWeblinkOpened && variant === SHARING_VARIANT.foldersBiz && (
                    <Button
                        mode="link"
                        appearance="accent-invariable"
                        onClick={onBackClick}
                        before={<Icon20ChevronLeft />}
                        className={styles.backButton}
                        data-qa-id="sharing-content-folders-back-button"
                    >
                        Назад
                    </Button>
                )}
                <div className={classNames(styles.header, styles.sharingTabs)} data-qa-id="sharing-header">
                    <span>{title}</span>
                    <NameComponent name={`«${item?.name}»`} truncate showTooltip />
                </div>
                {/* tempexp_17779-start */}
                {Object.entries(availableTabs).length > 1 && (
                    <TabMenu align={ETabMenuAlign.center} mod={ETabMenuMod.folderSharing} qaId="sharing-tab-menu">
                        {Object.entries(availableTabs).map(([value, { name }]) => {
                            return (
                                <TabMenuItem
                                    key={name}
                                    name={`${name}`}
                                    onClick={(value) => {
                                        setActiveTab(value);
                                        if (value === SharingTabs.WEBLINK) {
                                            sendSharingContentAnalytics({ action: 'switch', label: 'click' });
                                        }
                                    }}
                                    value={value}
                                    active={activeTab === value}
                                    disabled={false}
                                    changeHash={false}
                                />
                            );
                        })}
                    </TabMenu>
                )}
                {/* tempexp_17779-end */}
                <div className={styles.content} ref={ref}>
                    {activeTab === SharingTabs.WEBLINK && SharingTabs.WEBLINK in availableTabs && (
                        // tempexp_17779-start
                        <SharingNewWeblinkTab
                            id={id}
                            onboardingEnabled={onboardingEnabled}
                            // tempexp_17779-end
                            item={item}
                            from={from}
                            isSharedOrMouted={isSharedOrMouted}
                            gaSuffix={gaSuffix}
                            noEdit={noEdit}
                            isOwn={!isPublic || isOwnPublic}
                            isPublic={isPublic}
                            isPhone={isPhone}
                            onUnpublish={onCloseAccess}
                            sendSharingContentAnalytics={sendSharingContentAnalytics}
                            withPromoTooltip={withPromoTooltip}
                            showListSharing={showSharing}
                            // tempexp_17779-start
                            foldSettings
                            // tempexp_17779-end
                            variant={props.variant}
                            folderName={folderName}
                            fromEditor={props.fromEditor}
                            formProps={props}
                            onCloseModal={onClose}
                        />
                    )}

                    {/* Отключаем фичу 'Пользователям облака' в Тутории*/}
                    {((activeTab === SharingTabs.CLOUD_USERS && SharingTabs.CLOUD_USERS in availableTabs) ||
                        (activeTab === SharingTabs.CLOUD_USERS_BIZ && SharingTabs.CLOUD_USERS_BIZ in availableTabs)) && (
                        // tempexp_17779-start
                        <SharingNewSharingTab
                            fetchedDataOnMount={fetchedDataOnMount}
                            setFetchedDataOnMount={setFetchedDataOnMount}
                            // tempexp_17779-end
                            onClose={onClose}
                            item={item}
                            from={from}
                            isPhone={isPhone}
                            isMounted={isMounted}
                            variant={props.variant}
                        />
                    )}

                    {activeTab === SharingTabs.WEBLINK_ACCESS_LIST && SharingTabs.WEBLINK_ACCESS_LIST in availableTabs && (
                        <SharingWeblinkIndividualAccessTab
                            id={id}
                            onboardingEnabled={onboardingEnabled}
                            item={item}
                            from={from}
                            isSharedOrMouted={isSharedOrMouted}
                            gaSuffix={gaSuffix}
                            noEdit={noEdit}
                            isOwn={!isPublic || isOwnPublic}
                            isPublic={isPublic}
                            isPhone={isPhone}
                            onUnpublish={onCloseAccess}
                            sendSharingContentAnalytics={sendSharingContentAnalytics}
                            withPromoTooltip={withPromoTooltip}
                            showListSharing={showSharing}
                            foldSettings
                            variant={props.variant}
                            folderName={folderName}
                            fromEditor={props.fromEditor}
                            formProps={props}
                            onCloseModal={onClose}
                            onOpenAccess={onBackClick}
                        />
                    )}
                </div>
            </div>
        );
    })
);

SharingContentFolders.displayName = 'SharingContentFolders';
